import * as uuid from 'uuid';
import { shallowRef } from 'vue';

import type { MapLayerAttribution, MapLayerBaseSettings } from '@nora-monorepo/nora-api-client';
import { SettingKey } from '@nora-monorepo/nora-api-client';
import type { LatitudeLongitude } from '@nora-monorepo/shared';
import { isNonEmptyString, NoraSupportedCoordinateFormats } from '@nora-monorepo/shared';
import type { NoraWebViewMapSource } from '@nora-monorepo/ui-shared';
import { useConfigStore } from '@nora-monorepo/ui-shared';

import type { CompositeLayerConfig, LayerConfig, LayerConfigInfo } from '#nora-ui/map/types/layer.types';
import type { MapConfig, MapConfigBaseMap } from '#nora-ui/map/types/map.types';
import { CompositeLayerId, MapMode } from '#nora-ui/map/types/map.types';

/**
 * Singleton class for setting up and managing map configurations and layers.
 * The reason for using a singleton is to ensure consistent map setup across the application, and the layer calculations
 * are only needed once per application lifecycle.
 */
export class MapSetup {
  /**
   * Singleton instance of MapSetup.
   */
  private static instance: MapSetup | undefined;

  /**
   * Web Mercator projection constant (EPSG:3857).
   */
  static readonly WEB_MERCATOR = NoraSupportedCoordinateFormats.WEB_MERCATOR;

  /**
   * Geographic coordinate system projection constant (EPSG:4326).
   */
  static readonly WGS84 = NoraSupportedCoordinateFormats.WGS84;

  /**
   * UTM Zone 32 projection constant (EPSG:32632)
   */
  static readonly UTM = NoraSupportedCoordinateFormats.UTM;

  /**
   * Geographic coordinate system projection constant (Lat/Lon Degrees, Minutes, Seconds (DMS))
   */
  static readonly LAT_LON_DMS = NoraSupportedCoordinateFormats.DMS;

  /**
   * Geographic coordinate system projection constant (Lat/Lon Degrees, Minutes (DMM))
   */
  static readonly LAT_LON_HDMM = NoraSupportedCoordinateFormats.HDMM;

  /**
   * MGRS projection constant (MGRS)
   */
  static readonly MGRS = NoraSupportedCoordinateFormats.MGRS;

  /**
   * Default center as [longitude, latitude] in EPSG:4326 (WGS84) - Oslo
   */
  // NOTE: This is not instantiated as LatLong because this data is stored JSON-first in localStorage
  static readonly MAP_DEFAULT_CENTER: LatitudeLongitude = { latitude: 59.91273, longitude: 10.74609 };

  /**
   * Default rotation in radians.
   */
  static readonly DEFAULT_ROTATION = 0;

  /**
   * Default zoom level.
   */
  static readonly DEFAULT_ZOOM = 10;

  /**
   * Default maximum zoom level for base maps.
   */
  static readonly BASE_MAP_DEFAULT_MAX_ZOOM = 19;

  /**
   * Predefined composite layers for organizing map layers.
   */
  private readonly COMPOSITE_LAYERS: CompositeLayerConfig[] = [
    // The layer for the bottom-most map that will always have 1 visible child
    {
      layerType: 'CompositeLayer',
      id: CompositeLayerId.BaseMaps,
      name: 'Base maps',
    },
    // The layer for the stackable overlay maps on top of the base map, ex traffic, area maps etc
    {
      layerType: 'CompositeLayer',
      id: CompositeLayerId.OverlayMaps,
      name: 'Overlay maps',
    },
    // The layer for the stackable util overlays such as dim, grid, etc
    {
      layerType: 'CompositeLayer',
      id: CompositeLayerId.UtilOverlays,
      name: 'Util overlays',
    },
    // The layer for NORA overlays such as events, files, drawlayers and tracks
    {
      layerType: 'CompositeLayer',
      id: CompositeLayerId.NoraOverlays,
      name: 'NORA overlays',
    },
  ];

  private configStore = useConfigStore();

  private baseMaps = shallowRef<LayerConfigInfo[]>([]);
  private overlayMaps = shallowRef<LayerConfig[]>([]);
  private offlineMaps = shallowRef<LayerConfig[]>([]);
  private attributionList: Map<string, MapLayerAttribution> = new Map();

  /**
   * Gets the singleton instance of MapSetup.
   * @returns The MapSetup instance.
   */
  static getInstance(): MapSetup {
    MapSetup.instance ??= new MapSetup();

    return MapSetup.instance;
  }

  /**
   * Creates an instance of MapSetup.
   */
  private constructor() {
    this.refreshFromSettings();
  }

  /**
   * Recalculate layer definitions from the latest settings in config store.
   */
  refreshFromSettings(): void {
    const { baseMaps, overlayMaps, attributionList } = this.calculateLayers();

    this.baseMaps.value = baseMaps;
    this.overlayMaps.value = overlayMaps;
    this.attributionList = attributionList;
  }

  /**
   * Calculates the base maps, overlay maps, and attributions from user settings.
   * @returns An object containing arrays of base maps, overlay maps, and a map of attributions.
   */
  private calculateLayers() {
    const userMaps = this.getUserMaps();

    const layers = userMaps.layers;
    const baseMaps: LayerConfigInfo[] = [];
    const overlayMaps: LayerConfig[] = [];
    const attributionList = new Map<string, MapLayerAttribution>();

    layers.forEach((map) => {
      const layerConfig: LayerConfig = { ...map.config };

      if (map.attribution) {
        attributionList.set(layerConfig.id, map.attribution);
      }

      const visible = map.defaultVisible ?? false;

      if (map.isOverlay) {
        overlayMaps.push({
          ...layerConfig,
          visible,
          parentLayerId: CompositeLayerId.OverlayMaps,
        });
      }
      else {
        const baseMapLayerConfig: LayerConfigInfo = {
          ...layerConfig,
          visible,
          maxZoom: map.maxZoom ?? MapSetup.BASE_MAP_DEFAULT_MAX_ZOOM,
          parentLayerId: CompositeLayerId.BaseMaps,
        };

        baseMaps.push(baseMapLayerConfig);
      }
    });

    return {
      baseMaps,
      overlayMaps,
      attributionList,
    };
  }

  /**
   * Creates a new map configuration with default settings.
   * @param operationId Optional operation ID to associate with the map configuration.
   * @returns A new MapConfig object.
   */
  createNewMapConfig(operationId?: string): MapConfig {
    const defaultBaseMapId = this.getDefaultBaseMapId(this.baseMaps.value);
    const convertedOverlayMaps = this.convertLayerConfigs(this.overlayMaps.value);
    const convertedOfflineMaps = this.convertLayerConfigs(this.offlineMaps.value);

    const mapConfig: MapConfig = {
      id: uuid.v4(),
      operations: isNonEmptyString(operationId) ? [{ id: operationId, visible: true }] : [],
      activeOperationId: operationId,
      baseMap: defaultBaseMapId,
      overlayMaps: convertedOverlayMaps,
      offlineMaps: convertedOfflineMaps,
      zoom: MapSetup.DEFAULT_ZOOM,
      rotation: MapSetup.DEFAULT_ROTATION,
      center: { ...getDefaultMapCenter() },
      projection: MapSetup.WEB_MERCATOR,
      mode: MapMode.Normal,
      headingUpMode: false,
    };

    return mapConfig;
  }

  /**
   * Gets the default base map ID from the list of base maps.
   * @param baseMaps An array of base map configurations.
   * @returns The ID of the default base map, or undefined if none are available.
   */
  getDefaultBaseMapId(baseMaps: LayerConfigInfo[]): string | undefined {
    if (baseMaps.length === 0) {
      return undefined;
    }

    const firstVisibleMap = baseMaps.find(
      mapDefinition => mapDefinition.visible,
    );

    const fallbackId = baseMaps[0]?.id;
    const defaultId = firstVisibleMap?.id ?? fallbackId;

    return defaultId;
  }

  /**
   * Gets the predefined composite layers.
   * @returns An array of composite layer information.
   */
  getCompositeLayers(): CompositeLayerConfig[] {
    return this.COMPOSITE_LAYERS;
  }

  /**
   * Gets the configured base maps.
   * @returns An array of base map configurations.
   */
  getBaseMaps(): LayerConfigInfo[] {
    return this.baseMaps.value;
  }

  /**
   * Gets the configured overlay maps.
   * @returns An array of overlay map configurations.
   */
  getOverlayMaps(): LayerConfig[] {
    return this.overlayMaps.value;
  }

  /**
   * Gets the configured offline maps.
   * @returns An array of offline map configurations.
   */
  getOfflineMaps(): LayerConfig[] {
    return this.offlineMaps.value;
  }

  /**
   * Gets the map layer attributions.
   * @returns A map of layer IDs to their attributions.
   */
  getAttributions() {
    return this.attributionList;
  }

  /**
   * Gets the attribution for a specific layer ID.
   * @param layerId The ID of the layer.
   * @returns The attribution information for the specified layer, or undefined if not found.
   */
  getAttributionForLayerId(layerId: string): MapLayerAttribution | undefined {
    return this.attributionList.get(layerId);
  }

  /**
   * Adds a new offline map to the list of offline maps.
   * @param mapSource The map source information to add as an offline map.
   */
  addOfflineMap(mapSource: NoraWebViewMapSource): void {
    const filteredOfflineMaps = this.offlineMaps.value.filter(
      map => map.id !== mapSource.id,
    );

    filteredOfflineMaps.push({
      id: mapSource.id,
      name: mapSource.name,
      layerType: 'GenericMapLayer',
      url: mapSource.mapSource,
      parentLayerId: CompositeLayerId.OverlayMaps,
    });

    this.offlineMaps.value = filteredOfflineMaps;
  }

  getConfigForBaseMapId(baseMapId: string): LayerConfigInfo | undefined {
    return this.baseMaps.value.find(
      baseMap => baseMap.id === baseMapId,
    );
  }

  /**
   * Converts layer configurations to MapConfigBaseMap format.
   * @param layers An array of LayerConfig objects.
   * @returns An array of MapConfigBaseMap objects.
   */
  private convertLayerConfigs(layers: LayerConfig[]): MapConfigBaseMap[] {
    return layers.map(({ id, visible, name }) => ({
      id,
      visible: visible ?? false,
      name: name ?? '',
    }));
  }

  /**
   * Retrieves user map configurations from settings.
   * @returns The user's map layer base settings.
   * @throws An error if no map configuration is found.
   */
  private getUserMaps(): MapLayerBaseSettings {
    const configMaps = this.configStore.getUserSetting(SettingKey.MAP_LAYER_BASE);

    if (!configMaps) {
      console.error('No map configuration found in user settings, this can mean no maps are configured or there was an error retrieving them.');
      console.error('If no maps are configured, please ask a NORA administrator to set up maps for your organization.');

      if (import.meta.env.DEV) {
        console.error('In development mode, run the following command to seed default maps:');
        console.error('bun run --filter @nora-monorepo/nora-api cli:db:seed:map');
      }

      throw new Error('No map configuration found');
    }

    return configMaps;
  }
}

/**
 * Gets the default map center coordinates from the configuration store, or falls back to a predefined default if not set
 */
export function getDefaultMapCenter(): LatitudeLongitude {
  return useConfigStore().get('mapUi.defaultCenter', MapSetup.MAP_DEFAULT_CENTER);
}
